import { z } from "zod";

import {
  BODY_AWARENESS_SENSITIVITY_OPTIONS,
  EARLY_SIGN_OPTIONS,
  ENVIRONMENTAL_SENSITIVITY_OPTIONS,
  LIGHT_SENSITIVITY_OPTIONS,
  MOVEMENT_SENSITIVITY_OPTIONS,
  SENSORY_DOMAINS,
  SMELL_SENSITIVITY_OPTIONS,
  SOUND_SENSITIVITY_OPTIONS,
  TASTE_SENSITIVITY_OPTIONS,
  TOUCH_SENSITIVITY_OPTIONS,
  WHAT_HELPS_OPTIONS,
} from "@/lib/sensory-profile/options";

function enumFromOptions<const T extends readonly { id: string }[]>(
  options: T,
) {
  type Id = T[number]["id"];
  return z.enum(options.map((option) => option.id) as [Id, ...Id[]]);
}

function objectFromOptions<
  const T extends readonly { id: string }[],
  S extends z.ZodType,
>(options: T, valueSchema: S) {
  type Id = T[number]["id"];
  const shape = Object.fromEntries(
    options.map((option) => [option.id, valueSchema]),
  ) as { [K in Id]: S };
  return z.object(shape);
}

function isValidUsE164(value: string) {
  return /^\+1\d{10}$/.test(value);
}

function isAtLeast18(dateOfBirth: Date, now = new Date()) {
  const eighteenthBirthday = new Date(dateOfBirth);
  eighteenthBirthday.setFullYear(eighteenthBirthday.getFullYear() + 18);
  return eighteenthBirthday <= now;
}

const ratingSchema = z
  .number({ error: "Choose a rating from 0 to 10" })
  .int()
  .min(0, { error: "Choose a rating from 0 to 10" })
  .max(10, { error: "Choose a rating from 0 to 10" });

export const basicInfoSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  dateOfBirth: z
    .date({ error: "Date of birth is required" })
    .max(new Date(), {
      error: "Date of birth cannot be in the future",
    })
    .refine((value) => isAtLeast18(value), {
      error: "You must be 18 or older to continue",
    }),
  email: z.email({ error: "Enter a valid email" }),
  phone: z
    .string()
    .trim()
    .min(1, "Phone is required")
    .refine((value) => isValidUsE164(value), {
      error: "Enter a valid US phone number",
    }),
  privacyAgreed: z.literal(true, {
    error: "Agree to the privacy policy to continue",
  }),
});

export const childInfoSchema = z.object({
  childNickname: z.string().trim().min(1, "Name or nickname is required"),
  childDateOfBirth: z
    .date({ error: "Date of birth is required" })
    .max(new Date(), {
      error: "Date of birth cannot be in the future",
    }),
});

export const sensitivitiesSchema = z.object({
  selectedSounds: z.array(enumFromOptions(SOUND_SENSITIVITY_OPTIONS)),
  additionalSounds: z.string(),
  selectedLightsAndVisualInput: z.array(
    enumFromOptions(LIGHT_SENSITIVITY_OPTIONS),
  ),
  additionalLightsAndVisualInput: z.string(),
  selectedTouchAndTextures: z.array(enumFromOptions(TOUCH_SENSITIVITY_OPTIONS)),
  additionalTouchAndTextures: z.string(),
  selectedSmells: z.array(enumFromOptions(SMELL_SENSITIVITY_OPTIONS)),
  additionalSmells: z.string(),
  selectedTastes: z.array(enumFromOptions(TASTE_SENSITIVITY_OPTIONS)),
  additionalTastes: z.string(),
  selectedMovement: z.array(enumFromOptions(MOVEMENT_SENSITIVITY_OPTIONS)),
  additionalMovement: z.string(),
  selectedBodyAwareness: z.array(
    enumFromOptions(BODY_AWARENESS_SENSITIVITY_OPTIONS),
  ),
  additionalBodyAwareness: z.string(),
  selectedEnvironmental: z.array(
    enumFromOptions(ENVIRONMENTAL_SENSITIVITY_OPTIONS),
  ),
  additionalEnvironmental: z.string(),
});

export const ratingsSchema = z.object({
  sensitivityRatings: objectFromOptions(SENSORY_DOMAINS, ratingSchema),
});

export type SensitivityRatings = z.infer<
  typeof ratingsSchema
>["sensitivityRatings"];

export const earlySignsSchema = z.object({
  selectedEarlySigns: z.array(enumFromOptions(EARLY_SIGN_OPTIONS)),
  additionalEarlySigns: z.string(),
});

export const whatHelpsSchema = z.object({
  selectedWhatHelps: z.array(enumFromOptions(WHAT_HELPS_OPTIONS)),
  additionalWhatHelps: z.string(),
});

export const preventionPlanSchema = z.object({
  triggerOne: z.string(),
  triggerTwo: z.string(),
  triggerThree: z.string(),
  earliestSigns: z.string(),
  whatUsuallyHelps: z.string(),
  overloadPlan: z.string(),
});

export const roadmapSchema = z.object({});

export const sensoryProfileSchema = basicInfoSchema
  .and(childInfoSchema)
  .and(sensitivitiesSchema)
  .and(ratingsSchema)
  .and(earlySignsSchema)
  .and(whatHelpsSchema)
  .and(preventionPlanSchema);

export type SensoryProfileValues = z.infer<typeof sensoryProfileSchema>;
